import { clearSessionTokens, getAccessToken, getCenterId, getRefreshToken, setSessionTokens } from '../utils/sessionStorage';
import { isNativePlatform, nativeApiBaseUrl } from '../platform/runtime';

const resolveApiBaseUrl = () => {
  if (isNativePlatform()) return nativeApiBaseUrl();

  const { protocol, hostname } = window.location;

  // En el dominio oficial nunca dependemos de una variable de build antigua.
  // La web de producción siempre habla con la API canónica.
  if (['pulsoraapp.com', 'www.pulsoraapp.com'].includes(hostname)) {
    return 'https://api.pulsoraapp.com/api';
  }
  if (hostname === 'beta.pulsoraapp.com') {
    return 'https://api-beta.pulsoraapp.com/api';
  }

  const configured = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  if (configured) {
    if (protocol === 'https:' && configured.startsWith('http://')) {
      return `https://${configured.slice('http://'.length)}`;
    }
    return configured;
  }

  if (['localhost', '127.0.0.1'].includes(hostname)) {
    return `${protocol}//${hostname}:8000/api`;
  }

  if (hostname.endsWith('.onrender.com')) {
    const serviceName = hostname.slice(0, -'.onrender.com'.length);
    if (!serviceName.endsWith('-api')) {
      return `https://${serviceName}-api.onrender.com/api`;
    }
  }

  return `${protocol}//${hostname}/api`;
};

const API_BASE_URL = resolveApiBaseUrl();
export const API_STATUS_EVENT = 'pulsora-api-status';
export const AUTH_EXPIRED_EVENT = 'pulsora-auth-expired';
let lastKnownApiStatus = null;
let refreshPromise = null;

const publishApiStatus = (online) => {
  if (lastKnownApiStatus === online) return;
  lastKnownApiStatus = online;
  window.dispatchEvent(new CustomEvent(API_STATUS_EVENT, { detail: { online, apiUrl: API_BASE_URL } }));
};

const publishAuthExpired = () => {
  clearSessionTokens();
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
};

const isAnonymousEndpoint = endpoint => (
  endpoint === '/health/' ||
  endpoint.startsWith('/public/') ||
  endpoint === '/auth/token/' ||
  endpoint === '/auth/token/refresh/' ||
  endpoint === '/auth/google/' ||
  endpoint === '/auth/apple/'
);

const jwtExpiration = token => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(window.atob(padded)).exp || null;
  } catch (_) {
    return null;
  }
};

const tokenIsExpired = token => {
  const exp = jwtExpiration(token);
  return exp ? (exp * 1000) <= Date.now() : false;
};

const connectionError = error => {
  const localDevelopment = !isNativePlatform() && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const result = new Error(localDevelopment
    ? 'No se pudo conectar con el servidor local de Pulsora. Verifica que el backend Django esté ejecutándose.'
    : 'No se pudo conectar con Pulsora. Inténtalo nuevamente en unos segundos.');
  result.code = 'API_UNAVAILABLE';
  result.cause = error;
  return result;
};

const refreshAccessToken = async () => {
  const refresh = getRefreshToken();
  if (!refresh) {
    publishAuthExpired();
    const error = new Error('Tu sesión expiró. Inicia sesión nuevamente.');
    error.code = 'AUTH_EXPIRED';
    error.status = 401;
    throw error;
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh }),
        });
        publishApiStatus(true);

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          if ([400, 401].includes(response.status)) {
            publishAuthExpired();
            const expired = new Error(data.detail || 'Tu sesión expiró. Inicia sesión nuevamente.');
            expired.code = 'AUTH_EXPIRED';
            expired.status = 401;
            throw expired;
          }
          const serverError = new Error(data.detail || data.message || 'No se pudo renovar la sesión.');
          serverError.status = response.status;
          throw serverError;
        }

        const tokens = await response.json();
        if (!tokens.access) throw new Error('El servidor no devolvió un token de acceso válido.');
        setSessionTokens({ access: tokens.access, refresh: tokens.refresh || refresh });
        return tokens.access;
      } catch (error) {
        if (error instanceof TypeError) {
          publishApiStatus(false);
          // Un corte de red o un Render despertando no destruye la sesión.
          // El usuario podrá reintentar cuando el backend vuelva a responder.
          throw connectionError(error);
        }
        throw error;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
};

/**
 * Petición genérica con renovación automática del JWT.
 * - Cada pestaña mantiene su propio refresh token en sessionStorage.
 * - Si el access token venció, se renueva antes de consultar la API.
 * - Si el servidor responde 401, se renueva una vez y se repite la petición.
 * - Los errores de red nunca cierran la sesión automáticamente.
 */
const request = async (endpoint, options = {}, attempt = 0, transientAttempt = 0) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const isFormData = options.body instanceof FormData;
  const anonymous = isAnonymousEndpoint(endpoint);

  if (!anonymous && getRefreshToken() && (!getAccessToken() || tokenIsExpired(getAccessToken()))) {
    await refreshAccessToken();
  }

  const access = anonymous ? null : getAccessToken();
  const hasBody = options.body !== undefined && options.body !== null;
  const config = {
    headers: {
      ...(!isFormData && hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
      ...(!anonymous && getCenterId() ? { 'X-Centro-ID': getCenterId() } : {}),
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    publishApiStatus(true);

    if (response.status === 401 && !anonymous && attempt === 0 && getRefreshToken()) {
      await refreshAccessToken();
      return request(endpoint, options, 1, transientAttempt);
    }

    if (!response.ok) {
      const method = String(options.method || 'GET').toUpperCase();
      if (method === 'GET' && [502, 503, 504].includes(response.status) && transientAttempt < 2) {
        await new Promise(resolve => window.setTimeout(resolve, 1200 * (transientAttempt + 1)));
        return request(endpoint, options, attempt, transientAttempt + 1);
      }
      const errorData = await response.json().catch(() => ({ message: `El servidor respondió con error ${response.status}.` }));
      const fieldMessage = typeof errorData === 'object'
        ? Object.entries(errorData).map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`).join(' · ')
        : '';
      const requestError = new Error(errorData.detail || errorData.message || fieldMessage || 'Ocurrió un error en la petición');
      requestError.status = response.status;
      requestError.data = errorData;

      if (response.status === 401 && !anonymous) publishAuthExpired();
      throw requestError;
    }

    if (response.status === 204) return null;
    return await response.json();
  } catch (error) {
    console.error('Error en la llamada a la API:', error);
    if (error instanceof TypeError) {
      publishApiStatus(false);
      throw connectionError(error);
    }
    throw error;
  }
};

const downloadRequest = async (endpoint, fallbackName = 'pulsora_export.zip', attempt = 0) => {
  if (getRefreshToken() && (!getAccessToken() || tokenIsExpired(getAccessToken()))) await refreshAccessToken();
  const headers = {
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
    ...(getCenterId() ? { 'X-Centro-ID': getCenterId() } : {}),
  };
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, { headers });
    publishApiStatus(true);
    if (response.status === 401 && attempt === 0 && getRefreshToken()) {
      await refreshAccessToken();
      return downloadRequest(endpoint, fallbackName, 1);
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || errorData.message || 'No se pudo descargar el archivo.');
      error.status = response.status;
      throw error;
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const matched = disposition.match(/filename="?([^";]+)"?/i);
    const name = matched?.[1] || fallbackName;
    const href = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href; anchor.download = name;
    document.body.appendChild(anchor); anchor.click(); anchor.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(href), 5000);
    return name;
  } catch (error) {
    if (error instanceof TypeError) { publishApiStatus(false); throw connectionError(error); }
    throw error;
  }
};

// Definimos las funciones específicas para cada recurso de la API
export const api = {
  social: {
    notifications: () =>
      request('/social/notifications/'),

    markSectionSeen: section =>
      request('/social/notifications/', {
        method: 'POST',
        body: JSON.stringify({ section }),
      }),

    communityFeed: () =>
      request('/social/community-feed/'),

    directory: (query = '') =>
      request(
        `/social/directory/?q=${encodeURIComponent(
          String(query || '').trim()
        )}`
      ),

    masterDirectory: (query = '') =>
      request(
        `/social/master/directory/?q=${encodeURIComponent(
          String(query || '').trim()
        )}`
      ),

    masterConversations: {
      list: () => request('/social/master/conversations/'),
      create: userId => request('/social/master/conversations/', { method: 'POST', body: JSON.stringify({ userId }) }),
      messages: id => request(`/social/master/conversations/${id}/messages/`),
      sendMessage: (id, body) => request(`/social/master/conversations/${id}/messages/`, { method: 'POST', body: JSON.stringify({ body }) }),
    },

    contactRequests: {
      list: () =>
        request('/social/contact-requests/'),

      create: data =>
        request('/social/contact-requests/', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      respond: (id, status) =>
        request(
          `/social/contact-requests/${id}/`,
          {
            method: 'PATCH',
            body: JSON.stringify({ status }),
          }
        ),
    },

    announcements: {
      list: () =>
        request('/social/announcements/'),

      create: formData =>
        request('/social/announcements/', {
          method: 'POST',
          body: formData,
        }),

      update: (id, formData) =>
        request(`/social/announcements/${id}/`, {
          method: 'PATCH',
          body: formData,
        }),

      remove: id =>
        request(`/social/announcements/${id}/`, {
          method: 'DELETE',
        }),

      react: (id, reaction) =>
        request(`/social/announcements/${id}/reaction/`, {
          method: 'POST',
          body: JSON.stringify({ reaction }),
        }),
    },
    conversations: {
      list: () =>
        request('/social/conversations/'),

      messages: id =>
        request(
          `/social/conversations/${id}/messages/`
        ),

      sendMessage: (id, body) =>
        request(
          `/social/conversations/${id}/messages/`,
          {
            method: 'POST',
            body: JSON.stringify({ body }),
          }
        ),

      revoke: id =>
        request(
          `/social/conversations/${id}/revoke/`,
          {
            method: 'POST',
          }
        ),

      state: id =>
        request(
          `/social/conversations/${id}/state/`
        ),

      setBlocked: (id, blocked) =>
        request(
          `/social/conversations/${id}/block/`,
          {
            method: 'POST',
            body: JSON.stringify({ blocked }),
          }
        ),

      report: (id, reason) =>
        request(
          `/social/conversations/${id}/report/`,
          {
            method: 'POST',
            body: JSON.stringify({ reason }),
          }
        ),

      clear: id =>
        request(
          `/social/conversations/${id}/delete/`,
          {
            method: 'POST',
          }
        ),
},

    moderation: {
      reports: (status = '') =>
        request(
          '/social/moderation/reports/' +
          (status
            ? '?status=' + encodeURIComponent(status)
            : '')
        ),

      summary: () =>
        request('/social/moderation/reports/summary/'),

      // PULSORA MASTER MEMBER ACTIONS V1
      memberActions: id =>
        request(
          '/social/moderation/reports/' +
          id +
          '/action/'
        ),

      applyAction: (id, data) =>
        request(
          '/social/moderation/reports/' +
          id +
          '/action/',
          {
            method: 'POST',
            body: JSON.stringify(data),
          }
        ),
      updateReport: (id, status) =>
        request('/social/moderation/reports/' + id + '/', {
          method: 'PATCH',
          body: JSON.stringify({ status }),
        }),
    },
},

  health: () => request('/health/'),
  public: {
    centers: ({ includeInactive = false } = {}) => {
      const query = includeInactive ? '?include_inactive=1' : '';
      return request(`/public/centros/${query}`, { cache: 'no-store' });
    },
    centerAvailability: (field, value) => request(`/public/centros/disponibilidad/?field=${encodeURIComponent(field)}&value=${encodeURIComponent(value)}`),
    activities: () => request('/public/actividades/'),
    pulsoraPlans: () => request('/public/planes-pulsora/', { cache: 'no-store' }),
    geocodingSearch: ({ q = '', countryCode = '', limit = 12 } = {}) => {
      const params = new URLSearchParams();
      params.set('q', q);
      if (countryCode) params.set('country_code', countryCode);
      if (limit) params.set('limit', String(limit));
      return request(`/public/geocoding/search/?${params.toString()}`);
    },
    geocodingReverse: (latitude, longitude) => request(`/public/geocoding/reverse/?lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`),
    registerCenter: data => request('/public/registro/centro/', { method: 'POST', body: JSON.stringify(data) }),
    registerStudent: data => request('/public/registro/alumno/', { method: 'POST', body: JSON.stringify(data) }),
    verifyRegistrationEmail: (verificationId, code) => request('/public/registro/verificar-email/', { method: 'POST', body: JSON.stringify({ verification_id: verificationId, code }) }),
    resendRegistrationEmail: verificationId => request('/public/registro/reenviar-codigo/', { method: 'POST', body: JSON.stringify({ verification_id: verificationId }) }),
    requestPasswordReset: email => request('/public/password-reset/', { method: 'POST', body: JSON.stringify({ email }) }),
    confirmPasswordReset: data => request('/public/password-reset/confirm/', { method: 'POST', body: JSON.stringify(data) }),
    requestAccountDeletion: email => request('/public/account-deletion/request/', { method: 'POST', body: JSON.stringify({ email }) }),
    confirmAccountDeletion: data => request('/public/account-deletion/confirm/', { method: 'POST', body: JSON.stringify(data) }),
    getInvitation: token => request(`/public/invitaciones/${token}/`),
    acceptInvitation: (token, data) => request(`/public/invitaciones/${token}/`, { method: 'POST', body: JSON.stringify(data) }),
  },
  auth: {
    login: (username, password, forceSession = false) => request('/auth/token/', {
      method: 'POST',
      body: JSON.stringify({ username: username.trim().toLowerCase(), password, force_session: Boolean(forceSession) }),
    }),
    googleLogin: (credential, forceSession = false) => request('/auth/google/', {
      method: 'POST',
      body: JSON.stringify({ credential, force_session: Boolean(forceSession) }),
    }),
    appleLogin: (credential, nonce, profile = {}, forceSession = false) => request('/auth/apple/', {
      method: 'POST',
      body: JSON.stringify({
        credential,
        nonce,
        given_name: profile.givenName || '',
        family_name: profile.familyName || '',
        force_session: Boolean(forceSession),
      }),
    }),
    logout: () => request('/auth/logout/', { method: 'POST' }),
    heartbeat: () => request('/auth/heartbeat/', { method: 'POST' }),
    me: () => request('/auth/me/'),
    deleteAccount: confirmation => request('/account/delete/', { method: 'POST', body: JSON.stringify({ confirmation }) }),
  },
  members: {
    getAll: () => request('/members/'),
    getById: (id) => request(`/members/${id}/`),
    getProfile: (id) => request(`/members/${id}/profile/`),
    create: (data) => request('/members/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/members/${id}/`, { method: 'PUT', body: JSON.stringify(data) }),
    uploadPhoto: (id, file) => {
      const data = new FormData();
      data.append('foto', file);
      return request(`/members/${id}/`, { method: 'PATCH', body: data });
    },
    assignPlan: (id, planId, fechaInicio = '') => request(`/members/${id}/asignar-plan/`, { method: 'POST', body: JSON.stringify({ plan_id: Number(planId), fecha_inicio: fechaInicio || undefined }) }),
    assignProfessor: (id, data = {}) => request(`/members/${id}/asignar-profesor/`, { method: 'POST', body: JSON.stringify(data) }),
    notify: (id, mensaje) => request(`/members/${id}/notificar/`, { method: 'POST', body: JSON.stringify({ mensaje }) }),
    search: query => request(`/members/buscar/?q=${encodeURIComponent(query)}`),
    lookupEmail: email => request(`/members/buscar-email/?email=${encodeURIComponent(email)}`),
    affiliateEmail: email => request('/members/afiliar-email/', { method: 'POST', body: JSON.stringify({ email }) }),
    delete: (id) => request(`/members/${id}/`, { method: 'DELETE' }),
  },
  programming: {
    getAll: () => request('/programaciones/'),
    getById: id => request(`/programaciones/${id}/`),
    create: data => request('/programaciones/', { method: 'POST', body: JSON.stringify(data) }),
  },
  scheduledClasses: {
    getAll: (centerId = '', scope = '', options = {}) => {
      const params = new URLSearchParams();
      if (centerId) params.set('centro_id', centerId);
      if (scope) params.set('scope', scope);
      if (options.lat !== undefined && options.lat !== null && options.lat !== '') params.set('lat', String(options.lat));
      if (options.lon !== undefined && options.lon !== null && options.lon !== '') params.set('lon', String(options.lon));
      return request(`/clases-programadas/${params.toString() ? `?${params.toString()}` : ''}`);
    },
    getById: id => request(`/clases-programadas/${id}/`),
    getMineCompat: () => request('/clases-programadas/mis-clases-compat/'),
    getSummary: id => request(`/clases-programadas/${id}/resumen/`),
    lateCheckinCandidates: (id, query = '') => request(`/clases-programadas/${id}/alumnos-disponibles-checkin/${query ? `?q=${encodeURIComponent(query)}` : ''}`),
    create: data => request('/clases-programadas/', { method: 'POST', body: JSON.stringify(data) }),
    createRecurring: data => request('/clases-programadas/recurrente/', { method: 'POST', body: JSON.stringify(data) }),
    processReminders: () => request('/clases-programadas/procesar-recordatorios/', { method: 'POST' }),
    remindInvitedProfiles: id => request(`/clases-programadas/${id}/recordar-invitados/`, { method: 'POST' }),
    update: (id, data, { allowOverlap = false } = {}) => request(`/clases-programadas/${id}/${allowOverlap ? '?permitir_solapamiento=1' : ''}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/clases-programadas/${id}/`, { method: 'DELETE' }),
    bulkDelete: ids => request('/clases-programadas/eliminar-lote/', { method: 'POST', body: JSON.stringify({ ids }) }),
    cancelClass: (id, motivo_cancelacion) => request(`/clases-programadas/${id}/cancelar-clase/`, { method: 'POST', body: JSON.stringify({ motivo_cancelacion }) }),
    reserve: (id, centerId = '') => request(`/clases-programadas/${id}/reserve/${centerId ? `?centro_id=${encodeURIComponent(centerId)}` : ''}`, { method: 'POST' }),
    cancelReservation: (id, centerId = '') => request(`/clases-programadas/${id}/cancel_reservation/${centerId ? `?centro_id=${encodeURIComponent(centerId)}` : ''}`, { method: 'POST' }),
    startQr: id => request(`/clases-programadas/${id}/start_qr/`, { method: 'POST' }),
    getQr: id => request(`/clases-programadas/${id}/qr_code/`),
    closeQr: id => request(`/clases-programadas/${id}/close_qr/`, { method: 'POST' }),
    manualCheckin: (id, memberId, confirmacionDoble = false) => request(`/clases-programadas/${id}/manual_checkin/`, { method: 'POST', body: JSON.stringify({ miembro_id: memberId, confirmacion_doble: Boolean(confirmacionDoble) }) }),
    updateAttendance: (id, memberId, estado, motivo = '') => request(`/clases-programadas/${id}/actualizar-asistencia/`, { method: 'POST', body: JSON.stringify({ miembro_id: memberId, estado, motivo }) }),
  },
  portal: {
    getSummary: () => request('/portal/resumen/'),
    getLinkedCenters: () => request('/portal/centros/vinculados/'),
    updateProfile: data => request('/portal/resumen/', { method: 'PATCH', body: data instanceof FormData ? data : JSON.stringify(data) }),
    searchCenters: ({ pais = '', ciudad = '', q = '', region_lat = '', region_lon = '', radius_km = '' } = {}) => {
      const params = new URLSearchParams();
      if (pais) params.set('pais', pais);
      if (ciudad) params.set('ciudad', ciudad);
      if (q) params.set('q', q);
      if (region_lat !== '' && region_lat !== null && region_lat !== undefined) params.set('region_lat', String(region_lat));
      if (region_lon !== '' && region_lon !== null && region_lon !== undefined) params.set('region_lon', String(region_lon));
      if (radius_km) params.set('radius_km', String(radius_km));
      return request(`/portal/centros/${params.toString() ? `?${params.toString()}` : ''}`);
    },
    getCenterProfile: centroId => request(`/portal/centros/${encodeURIComponent(centroId)}/perfil/`),
    requestCenter: (centroId, canal = 'email') => request('/portal/centros/', { method: 'POST', body: JSON.stringify({ centro_id: Number(centroId), canal_notificacion: canal }) }),
    leaveCenter: centroId => request('/portal/centros/', { method: 'DELETE', body: JSON.stringify({ centro_id: Number(centroId) }) }),
  },
  attendance: {
    getMine: () => request('/asistencia/'),
    scan: token => request('/asistencia/escanear/', { method: 'POST', body: JSON.stringify({ token }) }),
  },
  dashboard: {
    get: (scope = 'center') => request(`/dashboard/?scope=${scope}`),
  },
  centerMetrics: {
    get: (filters = {}) => {
      const params = new URLSearchParams();
      const normalized = typeof filters === 'string'
        ? { scope: 'month', month: filters }
        : (filters || {});
      ['scope', 'metric', 'date', 'month', 'year'].forEach(key => {
        const value = normalized[key];
        if (value !== '' && value !== null && value !== undefined) params.set(key, String(value));
      });
      return request(`/administration/metrics/${params.toString() ? `?${params.toString()}` : ''}`);
    },
  },
  global: {
    dashboard: () => request('/global/dashboard/'),
    evolution: (metric, granularity) => request(`/global/evolution/?metric=${metric}&granularity=${granularity}`),
    members: (type = '') => request(`/global/members/${type ? `?type=${encodeURIComponent(type)}` : ''}`),
    contactMember: (type, id, data) => request(`/global/members/${encodeURIComponent(type)}/${id}/contact/`, { method: 'POST', body: JSON.stringify(data) }),
    deleteMember: (type, id, reason) => request(`/global/members/${encodeURIComponent(type)}/${id}/delete/`, { method: 'POST', body: JSON.stringify({ reason }) }),
    exportData: () => downloadRequest('/global/export/', 'pulsora_export.zip'),
    leaderboard: () => request('/global/leaderboard/'),
    comments: {
      getAll: () => request('/global/comentarios/'),
      update: (id, data) => request(`/global/comentarios/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
      delete: id => request(`/global/comentarios/${id}/`, { method: 'DELETE' }),
    },
  },
  billing: {
    summary: () => request('/billing/summary/'),
    usage: () => request('/billing/usage/'),
    plans: {
      getAll: () => request('/billing/plans/'),
      create: data => request('/billing/plans/', { method: 'POST', body: JSON.stringify(data) }),
      update: (id, data) => request(`/billing/plans/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
      archive: id => request(`/billing/plans/${id}/`, { method: 'DELETE' }),
    },
    codes: {
      getAll: () => request('/billing/codes/'),
      create: data => request('/billing/codes/', { method: 'POST', body: JSON.stringify(data) }),
      update: (id, data) => request(`/billing/codes/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
      archive: id => request(`/billing/codes/${id}/`, { method: 'DELETE' }),
    },
    subscriptions: {
      getAll: () => request('/billing/subscriptions/'),
      update: (id, data) => request(`/billing/subscriptions/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
      createInvoice: id => request(`/billing/subscriptions/${id}/invoice/`, { method: 'POST' }),
      registerPayment: (id, data) => request(`/billing/subscriptions/${id}/register-payment/`, { method: 'POST', body: JSON.stringify(data) }),
    },
    invoices: {
      getAll: (centerId = '') => request(`/billing/invoices/${centerId ? `?centro_id=${encodeURIComponent(centerId)}` : ''}`),
      markPaid: (id, data = {}) => request(`/billing/invoices/${id}/mark-paid/`, { method: 'POST', body: JSON.stringify(data) }),
    },
    mine: () => request('/billing/my-subscription/'),
    selectPlan: plan_id => request('/billing/my-subscription/select-plan/', { method: 'POST', body: JSON.stringify({ plan_id }) }),
    applyCode: codigo => request('/billing/my-subscription/apply-code/', { method: 'POST', body: JSON.stringify({ codigo }) }),
  },
  classes: {
    getAll: () => request('/clases/'),
    create: data => request('/clases/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/clases/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/clases/${id}/`, { method: 'DELETE' }),
  },
  staff: {
    getAll: () => request('/personal/'),
    create: data => request('/personal/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/personal/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/personal/${id}/`, { method: 'DELETE' }),
  },
  centerLocations: {
    getAll: () => request('/ubicaciones-centro/'),
    create: data => request('/ubicaciones-centro/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/ubicaciones-centro/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/ubicaciones-centro/${id}/`, { method: 'DELETE' }),
  },
  payments: {
    getAll: () => request('/pagos/'),
    getSummary: () => request('/pagos/resumen/'),
    getBillingConfig: () => request('/pagos/configuracion-facturacion/'),
    updateBillingConfig: data => request('/pagos/configuracion-facturacion/', { method: 'PATCH', body: JSON.stringify(data) }),
    getById: id => request(`/pagos/${id}/`),
    create: data => request('/pagos/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/pagos/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/pagos/${id}/`, { method: 'DELETE' }),
    requestMembership: (data, centerId = '') => request(`/pagos/solicitar-mensualidad/${centerId ? `?centro_id=${encodeURIComponent(centerId)}` : ''}`, { method: 'POST', body: JSON.stringify(data) }),
    upgradeMembership: (data, centerId = '') => request(`/pagos/upgrade-membresia/${centerId ? `?centro_id=${encodeURIComponent(centerId)}` : ''}`, { method: 'POST', body: JSON.stringify(data) }),
    requestSingleClass: data => request('/pagos/pagar-clase/', { method: 'POST', body: JSON.stringify(data) }),
    uploadProof: (id, file) => {
      const data = new FormData();
      data.append('comprobante', file);
      return request(`/pagos/${id}/subir-comprobante/`, { method: 'POST', body: data });
    },
    review: (id, decision, notas = '', fechaInicio = '') => request(`/pagos/${id}/revisar/`, { method: 'POST', body: JSON.stringify({ decision, notas, fecha_inicio: fechaInicio || undefined }) }),
    reverse: (id, motivo) => request(`/pagos/${id}/revertir/`, { method: 'POST', body: JSON.stringify({ motivo }) }),
    getRegistrationOptions: () => request('/pagos/opciones-registro/'),
    registerExternal: data => request('/pagos/registrar-externo/', { method: 'POST', body: JSON.stringify(data) }),
    reportExternal: data => request('/pagos/reportar-externo/', { method: 'POST', body: JSON.stringify(data) }),
    getBalance: (centerId = '') => request(`/pagos/saldo/${centerId ? `?centro_id=${encodeURIComponent(centerId)}` : ''}`),
    useBalanceForClass: clase_programada_id => request('/pagos/usar-saldo-clase/', { method: 'POST', body: JSON.stringify({ clase_programada_id: Number(clase_programada_id) }) }),
  },
  memberInvoices: {
    getAll: () => request('/facturas-miembro/'),
    getById: id => request(`/facturas-miembro/${id}/`),
    download: (id, number = '') => downloadRequest(`/facturas-miembro/${id}/descargar/`, `Factura-${number || id}.pdf`),
  },
  invitations: {
    getAll: () => request('/invitaciones-perfil/'),
    create: data => request('/invitaciones-perfil/', { method: 'POST', body: JSON.stringify(data) }),
    remind: id => request(`/invitaciones-perfil/${id}/recordar/`, { method: 'POST' }),
    cancel: id => request(`/invitaciones-perfil/${id}/`, { method: 'DELETE' }),
  },
  memberImport: {
    getAll: () => request('/administration/member-import/'),
    preview: file => {
      const data = new FormData();
      data.append('archivo', file);
      data.append('preview', 'true');
      return request('/administration/member-import/', { method: 'POST', body: data });
    },
    upload: (file, confirmacion = true, mapping = {}) => {
      const data = new FormData();
      data.append('archivo', file);
      data.append('confirmacion', confirmacion ? 'true' : 'false');
      data.append('mapping', JSON.stringify(mapping));
      return request('/administration/member-import/', { method: 'POST', body: data });
    },
    invitePending: (ids = [], reenviar = false) => request('/administration/member-import/invite/', {
      method: 'POST', body: JSON.stringify({ ids, reenviar }),
    }),
  },
  subscriptions: {
    getAll: () => request('/suscripciones-miembro/'),
    create: data => request('/suscripciones-miembro/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/suscripciones-miembro/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/suscripciones-miembro/${id}/`, { method: 'DELETE' }),
  },
  membershipPlans: {
    getAll: (centerId = '') => request(`/planes-membresia/${centerId ? `?centro_id=${encodeURIComponent(centerId)}` : ''}`),
    create: data => request('/planes-membresia/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/planes-membresia/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/planes-membresia/${id}/`, { method: 'DELETE' }),
  },
  systemUsers: {
    getAll: () => request('/usuarios-sistema/'),
    create: data => request('/usuarios-sistema/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/usuarios-sistema/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/usuarios-sistema/${id}/`, { method: 'DELETE' }),
    assignRole: (email, role) => request('/usuarios-sistema/asignar-rol/', { method: 'POST', body: JSON.stringify({ email, role }) }),
    removeRole: (id, role) => request(`/usuarios-sistema/${id}/quitar-rol/`, { method: 'POST', body: JSON.stringify({ role }) }),
  },
  profiles: {
    member: id => request(`/profiles/members/${id}/`),
  },
  joinRequests: {
    getAll: () => request('/solicitudes-afiliacion/'),
    markSeen: () => request('/solicitudes-afiliacion/marcar-vistas/', { method: 'POST' }),
    approve: (id, mensaje = '') => request(`/solicitudes-afiliacion/${id}/approve/`, { method: 'POST', body: JSON.stringify({ mensaje }) }),
    reject: (id, mensaje = '') => request(`/solicitudes-afiliacion/${id}/reject/`, { method: 'POST', body: JSON.stringify({ mensaje }) }),
    delete: id => request(`/solicitudes-afiliacion/${id}/`, { method: 'DELETE' }),
  },
  notifications: {
    getAll: () => request('/notificaciones/'),
    registerDevice: data => request('/notificaciones/registrar-dispositivo/', { method: 'POST', body: JSON.stringify(data) }),
    unregisterDevice: data => request('/notificaciones/desregistrar-dispositivo/', { method: 'POST', body: JSON.stringify(data) }),
    markRead: (ids = []) => request('/notificaciones/marcar-leidas/', { method: 'POST', body: JSON.stringify({ ids }) }),
    getAttention: () => request('/notificaciones/pendientes-atencion/'),
    markAttentionReviewed: area => request('/notificaciones/atencion-revisada/', { method: 'POST', body: JSON.stringify({ area }) }),
  },
  support: {
    getAll: () => request('/soporte/'),
    getById: id => request(`/soporte/${id}/`),
    getOptions: () => request('/soporte/opciones/'),
    create: data => request('/soporte/', { method: 'POST', body: JSON.stringify(data) }),
    reply: (id, mensaje) => request(`/soporte/${id}/responder/`, { method: 'POST', body: JSON.stringify({ mensaje }) }),
    close: id => request(`/soporte/${id}/cerrar/`, { method: 'POST' }),
    reopen: id => request(`/soporte/${id}/reabrir/`, { method: 'POST' }),
  },
  cardMethods: {
    getAll: () => request('/metodos-tarjeta/'),
    createTokenized: data => request('/metodos-tarjeta/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/metodos-tarjeta/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/metodos-tarjeta/${id}/`, { method: 'DELETE' }),
  },
  centers: {
    getAll: () => request('/centros/'),
    getById: id => request(`/centros/${id}/`),
    create: data => request('/centros/', { method: 'POST', body: data instanceof FormData ? data : JSON.stringify(data) }),
    update: (id, data) => request(`/centros/${id}/`, { method: 'PATCH', body: data instanceof FormData ? data : JSON.stringify(data) }),
    approveVerification: id => request(`/centros/${id}/aprobar-verificacion/`, { method: 'POST', body: JSON.stringify({}) }),
    rejectVerification: (id, motivo) => request(`/centros/${id}/rechazar-verificacion/`, { method: 'POST', body: JSON.stringify({ motivo }) }),
    archive: id => request(`/centros/${id}/archivar/`, { method: 'POST', body: JSON.stringify({}) }),
    restore: (id, data = {}) => request(`/centros/${id}/restaurar/`, { method: 'POST', body: JSON.stringify(data) }),
    hardDelete: id => request(`/centros/${id}/eliminar-definitivamente/`, { method: 'POST', body: JSON.stringify({}) }),
    deleteProfile: (id, confirmation) => request(`/centros/${id}/eliminar-perfil/`, { method: 'POST', body: JSON.stringify({ confirmation }) }),
    getMembershipNotifications: id => request(`/centros/${id}/notificaciones-membresia/`),
    updateMembershipNotifications: (id, config) => request(`/centros/${id}/notificaciones-membresia/`, { method: 'PATCH', body: JSON.stringify({ config }) }),
    uploadPhotos: (id, files) => {
      const data = new FormData();
      Array.from(files || []).forEach(file => data.append('fotos', file));
      return request(`/centros/${id}/fotos/`, { method: 'POST', body: data });
    },
    deletePhoto: (id, photoId) => request(`/centros/${id}/fotos/${photoId}/`, { method: 'DELETE' }),
    visibilityAccess: (id, q = '') => request(`/centros/${id}/accesos-visibilidad/${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    addVisibilityAccess: (id, usuarioId) => request(`/centros/${id}/accesos-visibilidad/`, { method: 'POST', body: JSON.stringify({ usuario_id: Number(usuarioId) }) }),
    removeVisibilityAccess: (id, usuarioId) => request(`/centros/${id}/accesos-visibilidad/`, { method: 'DELETE', body: JSON.stringify({ usuario_id: Number(usuarioId) }) }),
  },
  specialties: { getAll: () => request('/especialidades/') },
  coachRatings: {
    getAll: () => request('/valoraciones-profesores/'),
    create: data => request('/valoraciones-profesores/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/valoraciones-profesores/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    ranking: (scope = 'local', specialty = '') => request(`/ranking-profesores/?scope=${scope}${specialty ? `&especialidad=${specialty}` : ''}`),
  },
  benchmarks: {
    getAll: (memberId = '') => request(`/benchmarks-miembro/${memberId ? `?miembro_id=${encodeURIComponent(memberId)}` : ''}`),
    create: data => request('/benchmarks-miembro/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/benchmarks-miembro/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    updateMark: (id, data) => request(`/benchmarks-miembro/${id}/actualizar-marca/`, { method: 'POST', body: JSON.stringify(data) }),
    delete: id => request(`/benchmarks-miembro/${id}/`, { method: 'DELETE' }),
  },
  results: {
    getAll: () => request('/resultados-clase/'),
    create: data => request('/resultados-clase/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/resultados-clase/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: id => request(`/resultados-clase/${id}/`, { method: 'DELETE' }),
  },
  rankings: {
    regional: ({ pais = '', ciudad = '', especialidad = '', centro_id = '' } = {}) => {
      const params = new URLSearchParams();
      if (pais) params.set('pais', pais);
      if (ciudad) params.set('ciudad', ciudad);
      if (especialidad) params.set('especialidad', especialidad);
      if (centro_id) params.set('centro_id', String(centro_id));
      return request(`/portal/rankings-regionales/${params.toString() ? `?${params.toString()}` : ''}`);
    },
  },
  leaderboard: {
    get: () => request('/clasificacion/'),
    award: type => request('/clasificacion/', { method: 'POST', body: JSON.stringify({ type }) }),
  },
  products: {
    getAll: () => request('/productos/'),
    create: data => request('/productos/', { method: 'POST', body: data instanceof FormData ? data : JSON.stringify(data) }),
    update: (id, data) => request(`/productos/${id}/`, { method: 'PATCH', body: data instanceof FormData ? data : JSON.stringify(data) }),
    delete: id => request(`/productos/${id}/`, { method: 'DELETE' }),
  },
  orders: {
    getAll: () => request('/pedidos/'),
    create: data => request('/pedidos/', { method: 'POST', body: JSON.stringify(data) }),
    updateStatus: (id, estado) => request(`/pedidos/${id}/actualizar-estado/`, { method: 'POST', body: JSON.stringify({ estado }) }),
  },

  personalProgramming: {
    ownerGet: (memberId, year, month) => request(`/members/${encodeURIComponent(memberId)}/programacion-personal/?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`),
    ownerCreate: (memberId, data) => request(`/members/${encodeURIComponent(memberId)}/programacion-personal/`, { method: 'POST', body: JSON.stringify(data) }),
    ownerUpdate: (memberId, workoutId, data) => request(`/members/${encodeURIComponent(memberId)}/programacion-personal/${encodeURIComponent(workoutId)}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    ownerDelete: (memberId, workoutId) => request(`/members/${encodeURIComponent(memberId)}/programacion-personal/${encodeURIComponent(workoutId)}/`, { method: 'DELETE' }),
    ownerCopyDay: (memberId, data) => request(`/members/${encodeURIComponent(memberId)}/programacion-personal/copiar-dia/`, { method: 'POST', body: JSON.stringify(data) }),
    ownerCopyWeek: (memberId, data) => request(`/members/${encodeURIComponent(memberId)}/programacion-personal/copiar-semana/`, { method: 'POST', body: JSON.stringify(data) }),
    mine: (centerId, year, month) => {
      const params = new URLSearchParams();
      if (centerId) params.set('centro_id', centerId);
      params.set('year', year);
      params.set('month', month);
      return request(`/portal/programacion-personal/?${params.toString()}`);
    },
    completeMine: (workoutId, completed, centerId = '') => request(`/portal/programacion-personal/${encodeURIComponent(workoutId)}/completar/${centerId ? `?centro_id=${encodeURIComponent(centerId)}` : ''}`, { method: 'POST', body: JSON.stringify({ completado: completed }) }),
  },

};
